import React, { Component } from 'react';
import * as Sentry from '@sentry/react';
import { withTranslation } from 'react-i18next';

import DeviceIdForm from './DeviceIdForm.jsx';
import BasicInfoForm from './BasicInfoForm.jsx';
import AccountCreateForm from './AccountCreateForm.jsx';
import AuthForm from './AuthForm.jsx';
import AmazonVineForm from './AmazonVineForm.jsx';
import AmazonVineFinalizeForm from './AmazonVineFinalizeForm.jsx';
import PlanSelectForm from './PlanSelectForm.jsx';
import PrepaidDeviceActivationForm from './PrepaidDeviceActivationForm.jsx';
import SuccessScreen from './SuccessScreen.jsx';
import { decodeActivationState, encodeActivationState } from './ActivationWizardState';
import logo from '../../../../images/shieldgps-logo1.svg';
import LanguageSelector from '../bits/LanguageSelector.jsx';

window.Sentry = Sentry;
if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://c03f6946d09147d1a64f6d7990cadba7@sentry.io/5175130',
    release: process.env.VERSION,
    environment: process.env.NODE_ENV,
  });
}

class ActivationWizard extends Component {
  state = {
    navStack: [
      'deviceIdForm',
    ],
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    countryCode: null,
    deviceEmei: '',
    deviceModel: null,

    // To support annual plan activation 
    // AND to support various prepaid plans
    deviceIsPrepaid: false,
    prepaidDurationDays: null,

    selectedPlanId: null,
    selectedPlanInterval: 'monthly',
    devicePlatform: null,
    deviceUserGuideUrl: null,
    languages: [
      { id: 'en', name: 'English' },
      { id: 'fr', name: 'Français' },
      { id: 'de', name: 'Deutsch' },
      { id: 'it', name: 'Italiano' },
    ],
    selectedLanguageId: null,
    validatedPromoCode: '',
    vineEnabled: false,
    isAmazonReviewer: false,

  }

  componentDidMount() {
    this.setState({
      selectedLanguageId: this.activateLanguage(),
    });


    const urlParams = new URLSearchParams(window.location.search);
    const initialState = urlParams.get('istate');
    const paymentResult = urlParams.get('presult');

    if (initialState) {
      const initialStateObj = decodeActivationState(initialState);
      this.setState(initialStateObj);

      if (paymentResult && paymentResult === 'success') {
        this.goToStep('successScreen');
      } else if (paymentResult && paymentResult === 'cancel') {
        // NOOP
      }
    }
  }

  encodeState() {
    return encodeActivationState(this.state);
  }

  activateLanguage = () => {
    const { i18n } = this.props;

    const supportedLanguages = this.state.languages.map(lang => lang.id);

    // i18n.languages should always contain at least 'en' since that is configured
    // as the fallback lang.
    return i18n.languages.find(lang => supportedLanguages.includes(lang));
  }

  goToStep = (stepId, obj) => {
    const newState = {
      navStack: this.state.navStack.concat(stepId),
    };

    if (obj) {
      // Allow callers of nextStep to optionally push up new state
      Object.assign(newState, obj);
    }

    this.setState(newState);
  }

  goBackNSteps = numSteps => () => {
    this.setState({
      navStack: this.state.navStack.slice(0, -1 * numSteps),
    });
  }

  handleChange = input => (e) => {
    this.setState({ [input]: e.target.value });
  }

  currentStep = () => this.state.navStack[this.state.navStack.length - 1];

  /** Sends user to plan selection phase of the flow. Which screen they see depends on a few things. */
  goToPreActivationStep = () => {
    if (this.state.deviceIsPrepaid) {
      return this.goToStep('prepaidDeviceActivationForm');
    }

    if (this.state.vineEnabled && this.state.isAmazonReviewer) {
      return this.goToStep('amazonVineForm');
    }

    return this.goToStep('planSelectForm');
  };

  renderStep = () => {
    const stepId = this.currentStep();

    switch (stepId) {
      case 'deviceIdForm':
        return (
          <DeviceIdForm
            onNext={newState => this.goToStep('basicInfoForm', newState)}
            values={this.state}
            handleChange={this.handleChange}
          />
        );
      case 'basicInfoForm':
        return (
          <BasicInfoForm

            // Info form makes decision on where next depending if user known or not
            onNext={isNewAccount => isNewAccount ? this.goToStep('accountCreateForm') : this.goToStep('authForm')}
            onBack={this.goBackNSteps(1)}
            handleChange={this.handleChange}
            values={this.state}
            vineEnabled={this.state.vineEnabled}
          />
        );
      case 'accountCreateForm':
        return (
          <AccountCreateForm
            onNext={() => this.goToPreActivationStep()}
            onBack={this.goBackNSteps(1)}
            handleChange={this.handleChange}
            values={this.state}
          />
        );
      case 'authForm':
        return (
          <AuthForm
            onNext={() => this.goToPreActivationStep()}
            onBack={this.goBackNSteps(1)}
            handleChange={this.handleChange}
            values={this.state}
          />
        );
      case 'amazonVineForm':
        return (
          <AmazonVineForm
            onNext={() => this.goToStep('amazonVineFinalizeForm')}
            onSkip={() => this.goToStep('planSelectForm')}

            // Form prior to this was either account create or auth, so go back to the form
            // before that.
            onBack={this.goBackNSteps(2)}
            handleChange={this.handleChange}
            values={this.state}
            />
        );
      case 'planSelectForm':
        return (
          /** 4Jul23: From the payment flow update a while back, the PlanSelectForm no longer has a next */
          <PlanSelectForm
            encodeState={() => this.encodeState()}
            // If state.isAmazonReviewer then we want to send user back just one step to the
            // Vine entry form (even though they must have hit skip). Otherwise we go back 2 steps
            // past the auth or create forms and to basicInfoForm
            onBack={() => this.state.isAmazonReviewer ? this.goBackNSteps(1)() : this.goBackNSteps(2)() }
            values={this.state}
            handleChange={this.handleChange}
          />
        );
      /** User will skip plan select and see this screen if there device has a prepaid plan. */
      case 'prepaidDeviceActivationForm':
        return (
          <PrepaidDeviceActivationForm
            onNext={newState => this.goToStep('successScreen', newState)}
            onBack={this.goBackNSteps(2)}
            values={this.state}
            handleChange={this.handleChange}
            />
        );
      case 'amazonVineFinalizeForm':
        return (
          <AmazonVineFinalizeForm
            onNext={newState => this.goToStep('successScreen', newState)}
            onBack={this.goBackNSteps(1)}
            handleChange={this.handleChange}
            values={this.state}
            />
        );
      case 'successScreen':
        return (
          <SuccessScreen
            devicePlatform={this.state.devicePlatform}
            userGuideUrl={this.state.deviceUserGuideUrl}
          />
        );
      default:
        return (
          <h1>Unknown step</h1>
        );
    }
  }

  renderStepProgress = () => {
    const { t } = this.props;

    if (this.currentStep() === 'successScreen') {
      return false;
    }

    return (
      <h3 className="pull-right">
        {t('step_description', { currentStep: this.state.navStack.length, totalSteps: 5 })}
      </h3>
    );
  }

  handleLanguageSelect = (languageId) => {
    this.setState({ selectedLanguageId: languageId });
    const { i18n } = this.props;
    i18n.changeLanguage(languageId);
  };

  render() {
    const { t } = this.props;

    return (
      <div className="container activationWizard" style={{ maxWidth: '500px' }}>
        <div className="logoHeader">
          <a href="/">
            <img src={logo}/>
          </a>
        </div>
        {this.renderStepProgress()}
        <h3>{t('title')}</h3>
        {this.renderStep()}
        <p className="form-text text-muted" style={{ marginTop: '5px' }}>
          {t('contact_us')} <a className="mailto" href="mailto:support@shieldgps.co">support@shieldgps.co</a>.
        </p>
        <LanguageSelector
          languages={this.state.languages}
          selectedLanguageId={this.state.selectedLanguageId}
          onSelect={this.handleLanguageSelect}
          style={{ position: 'relative', left: '-6px' }}
        />
      </div>
    );
  }
}

export default withTranslation('activation_wizard')(ActivationWizard);
